import { RefObject, useLayoutEffect, useRef, useState } from 'react'

/**
 * Tells whether the text inside an element is cut off (ellipsis / clipped overflow).
 *
 * Attach the returned ref to the element that carries the `noWrap` / `text-overflow: ellipsis`
 * styling. The check re-runs when the element is resized and whenever `content` changes —
 * pass whatever the element renders (usually its text) so a new value is measured again.
 * Where `ResizeObserver` is unavailable (e.g. jsdom) it is measured on mount and on content change only.
 */
export const useIsTextTruncated = <T extends HTMLElement = HTMLElement>(content?: unknown): [RefObject<T>, boolean] => {
  const ref = useRef<T>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return undefined

    const measure = () => setIsTruncated(element.scrollWidth > element.clientWidth)
    measure()

    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [content])

  return [ref, isTruncated]
}
